import { useEffect, useRef, useState, useCallback } from "react";
import { setMoveSoundVolume } from "../../../../utils/audio/audioManager";
import "./Volume.scss";
import { useGame } from "../../../../context/GameContext";
import { VolumeUtils } from "../../../../utils/VolumeUtils";
import { VolumeLabelString } from "../../../../types/general";

type VolumeSliderProps = {
    navRow?: number;
    navCol?: number;
};

const VolumeSlider = ({ navRow, navCol }: VolumeSliderProps) => {
    // Global state for game settings
    const { gameState, setGameState } = useGame();

    // Refs for direct DOM access to slider and knob elements
    const sliderRef = useRef<HTMLDivElement>(null);
    const knobRef = useRef<HTMLImageElement>(null);

    // --- Component State ---
    const [isDragging, setIsDragging] = useState(false);
    const [knobPosition, setKnobPosition] = useState(0); // Current visual position
    const [dragStartOffset, setDragStartOffset] = useState(0); // Offset from cursor to knob center when drag starts
    const [isSliderLoaded, setIsSliderLoaded] = useState(false);
    const [isKnobLoaded, setIsKnobLoaded] = useState(false);

    /**
     * Calculates the key dimensions of the slider track.
     */
    const getSliderMetrics = useCallback(() => {
        if (!sliderRef.current || !knobRef.current) return null;

        const sliderWidth = sliderRef.current.offsetWidth;
        const knobWidth = knobRef.current.offsetWidth;

        // These percentages define the draggable area
        const paddingLeft = sliderWidth * 0.102;
        const paddingRight = sliderWidth * 0.108;

        const usableWidth = sliderWidth - paddingLeft - paddingRight;

        // Track boundaries
        const trackStart = paddingLeft;
        const trackEnd = sliderWidth - paddingRight;

        return { sliderWidth, knobWidth, usableWidth, trackStart, trackEnd };
    }, []);

    /**
     * Converts a volume level into a pixel position for the knob.
     */
    const getPositionForVolume = useCallback(
        (volume: VolumeLabelString) => {
            const metrics = getSliderMetrics();
            if (!metrics) return 0;

            const { usableWidth, trackStart } = metrics;
            const volumeLevel = VolumeUtils.fromLabel(volume);
            const volumeRatio = VolumeUtils.toPositionRatio(volumeLevel);
            const position = trackStart + volumeRatio * usableWidth;

            return position;
        },
        [getSliderMetrics],
    );

    /**
     * Converts a pixel position to the nearest volume level
     */
    const getVolumeForPosition = useCallback(
        (position: number) => {
            const metrics = getSliderMetrics();
            if (!metrics) return gameState.volume ?? "Medium";

            const { usableWidth, trackStart, trackEnd } = metrics;

            // Clamp position to track bounds
            const clampedPosition = Math.max(trackStart, Math.min(position, trackEnd));
            const relativePosition = clampedPosition - trackStart;
            const positionRatio = relativePosition / usableWidth;

            const volumeLevel = VolumeUtils.fromPositionRatio(positionRatio);
            return VolumeUtils.getLabel(volumeLevel);
        },
        [getSliderMetrics, gameState.volume],
    );

    /**
     * Updates the knob position during dragging (smooth movement)
     */
    const updateKnobPosition = useCallback(
        (clientX: number) => {
            if (!sliderRef.current) return;

            const sliderRect = sliderRef.current.getBoundingClientRect();
            const metrics = getSliderMetrics();
            if (!metrics) return;

            const { trackStart, trackEnd } = metrics;

            // Calculate desired position based on cursor, accounting for the drag offset
            const desiredPosition = clientX - sliderRect.left - dragStartOffset;

            // Clamp to track boundaries
            const clampedPosition = Math.max(trackStart, Math.min(desiredPosition, trackEnd));

            setKnobPosition(clampedPosition);
        },
        [getSliderMetrics, dragStartOffset],
    );

    /**
     * Snaps to the nearest volume level and updates game state
     */
    const snapToNearestVolume = useCallback(() => {
        // Don't snap if slider hasn't been properly initialized
        if (!isSliderLoaded || !isKnobLoaded) return;

        const nearestVolume = getVolumeForPosition(knobPosition);
        const snappedPosition = getPositionForVolume(nearestVolume);

        setKnobPosition(snappedPosition);

        // Update game state if volume changed
        if (nearestVolume !== gameState.volume) {
            setGameState((prev) => ({ ...prev, volume: nearestVolume }));
            const volumeLevel = VolumeUtils.fromLabel(nearestVolume);
            setMoveSoundVolume(VolumeUtils.toNormalized(volumeLevel));
        }
    }, [
        knobPosition,
        getVolumeForPosition,
        getPositionForVolume,
        gameState.volume,
        setGameState,
        isSliderLoaded,
        isKnobLoaded,
    ]);

    // --- Event Handlers ---

    const handleMouseDown = (e: React.MouseEvent) => {
        if (!sliderRef.current || !knobRef.current) return;

        const knobRect = knobRef.current.getBoundingClientRect();

        // Calculate offset from cursor to knob center
        const knobCenterX = knobRect.left + knobRect.width / 2;
        const offset = e.clientX - knobCenterX + 50;

        setDragStartOffset(offset);
        setIsDragging(true);
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        if (!sliderRef.current || !knobRef.current) return;

        const knobRect = knobRef.current.getBoundingClientRect();

        // Calculate offset from touch to knob center
        const knobCenterX = knobRect.left + knobRect.width / 2;
        const offset = e.touches[0].clientX - knobCenterX;

        setDragStartOffset(offset);
        setIsDragging(true);
    };

    const handleDragEnd = useCallback(() => {
        // Only snap if we were actually dragging
        if (isDragging) {
            snapToNearestVolume();
        }
        setIsDragging(false);
        setDragStartOffset(0);
    }, [snapToNearestVolume, isDragging]);

    /**
     * Effect to handle dragging logic.
     */
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (isDragging) {
                e.preventDefault();
                updateKnobPosition(e.clientX);
            }
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (isDragging) {
                e.preventDefault();
                updateKnobPosition(e.touches[0].clientX);
            }
        };

        const handleMouseUp = () => {
            if (isDragging) handleDragEnd();
        };

        const handleTouchEnd = () => {
            if (isDragging) handleDragEnd();
        };

        if (isDragging) {
            window.addEventListener("mousemove", handleMouseMove, { passive: false });
            window.addEventListener("touchmove", handleTouchMove, { passive: false });
            window.addEventListener("mouseup", handleMouseUp);
            window.addEventListener("touchend", handleTouchEnd);
        }

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("touchmove", handleTouchMove);
            window.removeEventListener("mouseup", handleMouseUp);
            window.removeEventListener("touchend", handleTouchEnd);
        };
    }, [isDragging, updateKnobPosition, handleDragEnd]);

    /**
     * Effect to set the initial knob position and handle resize.
     */
    useEffect(() => {
        if (isSliderLoaded && isKnobLoaded) {
            const setInitialPosition = () => {
                setKnobPosition(getPositionForVolume(gameState.volume ?? "Medium"));
            };
            setInitialPosition();
            window.addEventListener("resize", setInitialPosition);
            return () => window.removeEventListener("resize", setInitialPosition);
        }
    }, [isSliderLoaded, isKnobLoaded, gameState.volume, getPositionForVolume]);

    /**
     * Handles keyboard navigation for accessibility.
     */
    const handleKeyDown = (e: React.KeyboardEvent<HTMLImageElement>) => {
        const current = gameState.volume ?? "Medium";
        const currentLevel = VolumeUtils.fromLabel(current);
        let nextLevel = currentLevel;

        if (e.key === "ArrowRight") {
            nextLevel = VolumeUtils.increment(currentLevel);
        } else if (e.key === "ArrowLeft") {
            nextLevel = VolumeUtils.decrement(currentLevel);
        } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
            e.preventDefault();
            const dir = e.key === "ArrowUp" ? -1 : 1;
            const nextRow = Number(navRow ?? "0") + dir;
            const selector = `[data-navrow="${nextRow}"][data-navcol="${navCol}"]`;
            const nextEl = document.querySelector(selector) as HTMLElement | null;
            if (nextEl) {
                nextEl.focus();
            }
            return;
        } else return;

        const nextVolumeLabel = VolumeUtils.getLabel(nextLevel);
        const nextPos = getPositionForVolume(nextVolumeLabel);
        setKnobPosition(nextPos);
        setGameState((prev) => ({ ...prev, volume: nextVolumeLabel }));
        setMoveSoundVolume(VolumeUtils.toNormalized(nextLevel));
    };

    return (
        <div className="slider-container">
            <div className="volume-wrapper">
                <div
                    className="slider-track"
                    ref={sliderRef}
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                >
                    <img
                        className="volume-slot"
                        src="/audio/knob/chess-volume-slot-gauge-5.webp"
                        alt="volume slot"
                        draggable={false}
                        onLoad={() => setIsSliderLoaded(true)}
                    />
                    <img
                        ref={knobRef}
                        className={`volume-knob ${isDragging ? "dragging" : ""}`}
                        src="/audio/knob/chess-volume-knob.webp"
                        alt="volume knob"
                        draggable={false}
                        style={{ left: `${knobPosition}px` }}
                        onKeyDown={handleKeyDown}
                        onLoad={() => setIsKnobLoaded(true)}
                        data-navrow={navRow}
                        data-navcol={navCol}
                        tabIndex={0}
                    />
                </div>
                <div className="volume-options">
                    <p>0</p>
                    <p>100</p>
                </div>
                <p className="volume-text">Volume</p>
            </div>
        </div>
    );
};

export default VolumeSlider;
